// 検索サジェスト（Findee）API パス・パラメータ・レスポンス型
// 対応ルーター: backend/routers/search_suggest.py

export const SearchSuggestApiPath = {
  GET_SETTINGS: '/api/sites/{site_id}/search-suggest/settings',
  UPDATE_SETTINGS: '/api/sites/{site_id}/search-suggest/settings',
  GET_DELIVERY: '/api/sites/{site_id}/search-suggest/delivery',
  UPDATE_DELIVERY: '/api/sites/{site_id}/search-suggest/delivery',
  LIST_WIDGET_KEYS: '/api/sites/{site_id}/search-suggest/widget-keys',
  CREATE_WIDGET_KEY: '/api/sites/{site_id}/search-suggest/widget-keys',
  UPDATE_WIDGET_KEY: '/api/sites/{site_id}/search-suggest/widget-keys/{key_id}',
  DELETE_WIDGET_KEY: '/api/sites/{site_id}/search-suggest/widget-keys/{key_id}',
  GET_EMBED_TAG: '/api/sites/{site_id}/search-suggest/embed-tag',
  REBUILD_DICT: '/api/sites/{site_id}/search-suggest/dictionary/rebuild',
  LIST_DICT_BUILDS: '/api/sites/{site_id}/search-suggest/dictionary/builds',
  LIST_SYNONYMS: '/api/sites/{site_id}/search-suggest/synonyms',
  CREATE_SYNONYM: '/api/sites/{site_id}/search-suggest/synonyms',
  UPDATE_SYNONYM: '/api/sites/{site_id}/search-suggest/synonyms/{synonym_id}',
  DELETE_SYNONYM: '/api/sites/{site_id}/search-suggest/synonyms/{synonym_id}',
  LIST_CORRECTIONS: '/api/sites/{site_id}/search-suggest/corrections',
  APPROVE_CORRECTION: '/api/sites/{site_id}/search-suggest/corrections/{correction_id}/approve',
  REJECT_CORRECTION: '/api/sites/{site_id}/search-suggest/corrections/{correction_id}/reject',
  GENERATE_CORRECTIONS: '/api/sites/{site_id}/search-suggest/corrections/generate',
  LIST_MANUAL_KEYWORDS: '/api/sites/{site_id}/search-suggest/manual-keywords',
  CREATE_MANUAL_KEYWORD: '/api/sites/{site_id}/search-suggest/manual-keywords',
  UPDATE_MANUAL_KEYWORD: '/api/sites/{site_id}/search-suggest/manual-keywords/{keyword_id}',
  DELETE_MANUAL_KEYWORD: '/api/sites/{site_id}/search-suggest/manual-keywords/{keyword_id}',
  LIST_ZERO_HIT: '/api/sites/{site_id}/search-suggest/zero-hit-keywords',
  LIST_POPULAR_KEYWORDS: '/api/sites/{site_id}/search-suggest/popular-keywords',
  GET_ANALYTICS: '/api/sites/{site_id}/search-suggest/analytics',
  PREVIEW: '/api/sites/{site_id}/search-suggest/preview'
} as const

export type SearchSuggestApiPath =
  (typeof SearchSuggestApiPath)[keyof typeof SearchSuggestApiPath]

// --- 設定① ---

export interface SuggestSettings {
  site_id: number
  enable_master: boolean
  enable_word: boolean
  enable_product: boolean
  enable_article: boolean
  enable_category: boolean
  enable_brand: boolean
  count_word: number
  count_product: number
  count_article: number
  count_category: number
  count_brand: number
  sort_order: string
  show_initial_popular: boolean
  normalize: boolean
  fuzzy: boolean
  fuzzy_threshold: number
  correction_mode: string
  personalize: boolean
  use_order_weight: boolean
  use_reco: boolean
}

export interface GetSuggestSettingsVars {
  site_id: number
}

export interface UpdateSuggestSettingsVars {
  site_id: number
  enable_master?: boolean
  enable_word?: boolean
  enable_product?: boolean
  enable_article?: boolean
  enable_category?: boolean
  enable_brand?: boolean
  count_word?: number
  count_product?: number
  count_article?: number
  count_category?: number
  count_brand?: number
  sort_order?: string
  show_initial_popular?: boolean
  normalize?: boolean
  fuzzy?: boolean
  fuzzy_threshold?: number
  correction_mode?: string
  personalize?: boolean
  use_order_weight?: boolean
  use_reco?: boolean
}

export type GetSuggestSettingsResponse = SuggestSettings
export type UpdateSuggestSettingsResponse = SuggestSettings

// --- 配信② ---

export interface SuggestDeviceDesign {
  count_word: number
  count_product: number
  count_category: number
  count_brand: number
  count_article: number
  show_product_name: boolean
  show_catchcopy: boolean
  catchcopy_length: number
  show_outline: boolean
  outline_length: number
  show_thumb: boolean
  show_price: boolean
  show_stock: boolean
  show_out_of_stock: boolean
  show_article_title: boolean
  show_article_category: boolean
  show_article_thumb: boolean
  show_article_topics: boolean
  topic_length: number
  show_article_created: boolean
  show_article_updated: boolean
  theme_color: string
  custom_css: string
}

export interface SuggestDelivery {
  site_id: number
  input_selector: string
  surface_pc: boolean
  surface_sp: boolean
  pc: SuggestDeviceDesign
  sp: SuggestDeviceDesign
  status: string
  version: number
  published_at: string | null
}

export interface GetSuggestDeliveryVars {
  site_id: number
}

export interface UpdateSuggestDeliveryVars {
  site_id: number
  input_selector?: string
  surface_pc?: boolean
  surface_sp?: boolean
  pc?: Partial<SuggestDeviceDesign>
  sp?: Partial<SuggestDeviceDesign>
}

export type GetSuggestDeliveryResponse = SuggestDelivery
export type UpdateSuggestDeliveryResponse = SuggestDelivery

// --- 配信タグ用 APIキー / 埋め込みタグ ---

export interface SuggestWidgetKey {
  id: number
  site_id: number
  name: string
  api_key: string
  allowed_domains: string[]
  rate_limit_per_minute: number
  enabled: boolean
  last_used_at: string | null
  created_at: string | null
}

export interface ListSuggestWidgetKeysVars {
  site_id: number
}

export interface CreateSuggestWidgetKeyVars {
  site_id: number
  name: string
  allowed_domains: string[]
  rate_limit_per_minute?: number
}

export type ListSuggestWidgetKeysResponse = SuggestWidgetKey[]
export type CreateSuggestWidgetKeyResponse = SuggestWidgetKey

export interface UpdateSuggestWidgetKeyVars {
  site_id: number
  key_id: number
  name?: string
  allowed_domains?: string[]
  rate_limit_per_minute?: number
  enabled?: boolean
}

export interface DeleteSuggestWidgetKeyVars {
  site_id: number
  key_id: number
}

export interface GetSuggestEmbedTagVars {
  site_id: number
}

export interface SuggestEmbedTag {
  distribution_code: string
  api_key: string
  input_selector: string
  snippet: string
  cv_snippet: string
}

export type GetSuggestEmbedTagResponse = SuggestEmbedTag

// --- 辞書ビルド ---

export interface RebuildSuggestDictVars {
  site_id: number
}

export interface RebuildSuggestDictResponse {
  started: boolean
  available: boolean
}

// --- ビルド履歴 ---

export interface SuggestDictBuild {
  id: number
  mode: string
  status: string
  term_count: number
  error: string | null
  started_at: string | null
  finished_at: string | null
}

export interface ListSuggestDictBuildsVars {
  site_id: number
}

export type ListSuggestDictBuildsResponse = SuggestDictBuild[]

// --- シノニム ---

export interface SuggestSynonym {
  id: number
  site_id: number
  base_term: string
  synonyms: string[]
}

export interface ListSuggestSynonymsVars {
  site_id: number
}

export type ListSuggestSynonymsResponse = SuggestSynonym[]

export interface CreateSuggestSynonymVars {
  site_id: number
  base_term: string
  synonyms: string[]
}

export interface UpdateSuggestSynonymVars {
  site_id: number
  synonym_id: number
  base_term?: string
  synonyms?: string[]
}

export interface DeleteSuggestSynonymVars {
  site_id: number
  synonym_id: number
}

// --- 補正辞書 ---

export interface SuggestCorrection {
  id: number
  site_id: number
  wrong: string
  right: string
  confidence: number
  source: string
  status: string
  created_at: string | null
}

export interface ListSuggestCorrectionsVars {
  site_id: number
  status?: string
}

export type ListSuggestCorrectionsResponse = SuggestCorrection[]

export interface CorrectionActionVars {
  site_id: number
  correction_id: number
}

export interface GenerateCorrectionsVars {
  site_id: number
}

export interface GenerateCorrectionsResponse {
  generated: number
}

// --- 手動キーワード ---

export interface SuggestManualKeyword {
  id: number
  site_id: number
  keyword: string
  target_url: string
  pinned: boolean
  start_at: string | null
  end_at: string | null
}

export interface ListSuggestManualKeywordsVars {
  site_id: number
}

export type ListSuggestManualKeywordsResponse = SuggestManualKeyword[]

export interface CreateSuggestManualKeywordVars {
  site_id: number
  keyword: string
  target_url?: string
  pinned?: boolean
  start_at?: string | null
  end_at?: string | null
}

export interface DeleteSuggestManualKeywordVars {
  site_id: number
  keyword_id: number
}

// --- 0件キーワード ---

export interface SuggestZeroHit {
  keyword: string
  count: number
  last_at: string | null
}

export interface ListSuggestZeroHitVars {
  site_id: number
}

export type ListSuggestZeroHitResponse = SuggestZeroHit[]

// --- 人気キーワード（入力前の初期表示・自動分） ---

export interface SuggestPopularKeyword {
  keyword: string
  count: number
}

export interface ListSuggestPopularKeywordsVars {
  site_id: number
  days?: number
}

export type ListSuggestPopularKeywordsResponse = SuggestPopularKeyword[]

// --- 成果レビュー ---

export interface SuggestTrendPoint {
  date: string
  zero_rate: number
  cvr: number
}

export interface SuggestRankingItem {
  term: string
  count: number
}

export interface SuggestAnalytics {
  period_days: number
  impressions: number
  clicks: number
  conversions: number
  zero_hits: number
  utilization: number
  ctr: number
  cvr: number
  zero_rate: number
  utilization_delta: number
  ctr_delta: number
  cvr_delta: number
  zero_rate_delta: number
  trend: SuggestTrendPoint[]
  ranking: SuggestRankingItem[]
}

// 公開サジェストAPI（プレビュー用）の応答型
export interface SuggestPreviewResponse {
  query: string
  did_you_mean: string | null
  words: { term: string; url?: string | null; pinned?: boolean }[]
  products: {
    id?: string
    name: string
    catchcopy?: string | null
    outline?: string | null
    price?: number | null
    image_url?: string | null
    product_url?: string | null
    in_stock?: boolean | null
  }[]
  categories: { id?: string; name: string; url?: string | null }[]
  brands: { id?: string; name: string; url?: string | null }[]
  articles: {
    id?: string
    title: string
    url?: string | null
    category?: string | null
    image_url?: string | null
    topics?: string[]
    created_at?: string | null
    updated_at?: string | null
  }[]
}

export interface GetSuggestPreviewVars {
  site_id: number
  q: string
  device?: string
}

export type GetSuggestPreviewResponse = SuggestPreviewResponse

export interface GetSuggestAnalyticsVars {
  site_id: number
  days?: number
}

export type GetSuggestAnalyticsResponse = SuggestAnalytics
